<template>
	<div v-if="show" v-test="'save-status-indicator'" class="save-status d-flex align-center ga-1">
		<template v-if="status === 'saving'">
			<v-progress-circular indeterminate size="14" width="2" color="primary" />
			<span class="save-status-text">Saving…</span>
		</template>
		<template v-else-if="status === 'error'">
			<v-icon size="small" color="error">icon-circle-slash</v-icon>
			<span class="save-status-text text-error">Couldn't save — we'll retry on your next change</span>
		</template>
		<template v-else>
			<v-icon size="small" color="success">icon-check-circle</v-icon>
			<span class="save-status-text">Last saved {{ label }}</span>
		</template>
	</div>
</template>

<script lang="ts" setup>
	import { computed, onUnmounted, ref, watch } from "vue";
	import { formatLastSaved } from "@/helpers/formatLastSaved";
	import type { SaveStatus } from "@/composables/useAutosave";

	const props = defineProps<{
		status: SaveStatus;
		lastSavedAt: number | null;
	}>();

	// Tick once a minute so "Last saved X minutes ago" stays current. Only armed once a save has
	// happened — otherwise the indicator is hidden and the relative label would have nothing to show,
	// so an idle entity page carries no background timer.
	const now = ref(Date.now());
	let tick: ReturnType<typeof setInterval> | null = null;
	function stopTick(): void {
		if (tick !== null) {
			clearInterval(tick);
			tick = null;
		}
	}
	watch(
		() => props.lastSavedAt,
		(savedAt) => {
			if (savedAt === null) {
				// Reset back to never-saved (e.g. entity switch) — drop the interval so no hidden timer lingers.
				stopTick();
			} else if (tick === null) {
				now.value = Date.now();
				tick = setInterval(() => {
					now.value = Date.now();
				}, 60_000);
			}
		},
		{ immediate: true }
	);
	onUnmounted(stopTick);

	// Only surface once something has actually saved (or while saving / on error) — never on first load.
	const show = computed<boolean>(() => props.status === "saving" || props.status === "error" || props.lastSavedAt !== null);

	const label = computed<string>(() => (props.lastSavedAt !== null ? formatLastSaved(props.lastSavedAt, now.value) : ""));
</script>

<style scoped lang="scss">
	.save-status {
		color: rgba(var(--v-theme-ink), 0.6);
		font-size: 0.8125rem;
		font-weight: 500;
		white-space: nowrap;
	}

	.save-status-text {
		line-height: 1;
	}
</style>
